import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import FullBlockingModal from '../common/FullBlockingModal';
import FreelancerProfileModalContent from '../freelancers/FreelancerProfileModalContent';
import { FreelancerSearchResult } from '../../types/search';
import { useAllianceStore } from '../../stores/allianceStore';
import { canRequestAlliance } from '../../utils/allianceUtils';
import { useProfileStore } from '../../stores/profileStore';
import { useHasPermission } from '../../hooks/useHasPermission';
import { useModal } from '../../context/ModalContext';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';
import WalletSummary from '../credits/WalletSummary';
import ProfileBioText from '../common/ProfileBioText';
import { useCreditStore } from '../../stores/creditStore';

interface AllianceRequestContentProps {
  freelancer: FreelancerSearchResult;
  onClose: () => void;
  onSetAllianceRequestId: (allianceRequestId: string) => void;
}

const AllianceRequestContent = ({
  freelancer,
  onClose,
  onSetAllianceRequestId,
}: AllianceRequestContentProps) => {
  const { t } = useTranslation(['alliances', 'common']);
  const { requestAlliance } = useAllianceStore();
  const { profile: currentProfile } = useProfileStore();
  const hasPermission = useHasPermission('request_alliance');
  const { balance, getActionCost, refreshBalance, fetchActionCosts, actionCosts } =
    useCreditStore();

  const { getAcceptedAllianceRequestCount } = useAllianceStore();

  const [allianceRequestMessage, setAllianceRequestMessage] = useState('');

  const [allianceRequestError, setAllianceRequestError] = useState<string | null>(null);
  const [sendingAllianceRequest, setSendingAllianceRequest] = useState(false);

  const [allianceRequestCount, setAllianceRequestCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(true);

  const canRequest = canRequestAlliance(
    freelancer.alliance_status,
    currentProfile?.id,
    freelancer.id,
    hasPermission
  );

  const requiresCredits =
    !loadingCount && allianceRequestCount !== null && allianceRequestCount >= 3;
  const sendAllianceRequestCost = getActionCost('send_alliance_request');
  const hasEnoughCredits = balance !== null && balance >= sendAllianceRequestCost;

  useEffect(() => {
    refreshBalance();
    if (!Object.keys(actionCosts).length) {
      fetchActionCosts();
    }
  }, [actionCosts, fetchActionCosts, refreshBalance]);

  const primaryAllianceButtonLabel = loadingCount
    ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
    : requiresCredits
      ? t('use_credit_and_send', {
          ns: 'alliances',
          defaultValue: 'Utiliser 1 jeton et envoyer',
        })
      : canRequest
        ? t('sendMyRequest', { ns: 'alliances', defaultValue: 'Envoyer ma demande' })
        : t(`alliance_statuses.${freelancer.alliance_status}`, {
            ns: 'common',
            defaultValue: 'Alliance en cours',
          });

  const handleRequestAlliance = async () => {
    try {
      setSendingAllianceRequest(true);
      setAllianceRequestError(null);
      const result = await requestAlliance(freelancer.id, allianceRequestMessage);
      if (!result.id) throw new Error(result.error);

      await refreshBalance();
      onSetAllianceRequestId(result.id);
    } catch (error) {
      console.error(error);
      const errorMessage = (error as { message: string }).message;
      if (errorMessage.includes('INSUFFICIENT_CREDITS_ERROR')) {
        setAllianceRequestError(
          t('allianceRequestInsufficientCredits', {
            ns: 'alliances',
            defaultValue:
              'Crédits insuffisants pour envoyer cette demande. Veuillez acheter des crédits.',
            count: sendAllianceRequestCost,
          })
        );
        await refreshBalance();
      } else {
        setAllianceRequestError(t('allianceRequestError', { ns: 'alliances' }));
      }
    } finally {
      setSendingAllianceRequest(false);
    }
  };

  useEffect(() => {
    const fetchRequestCount = async () => {
      if (currentProfile?.id) {
        setLoadingCount(true);
        try {
          const requestCount = await getAcceptedAllianceRequestCount(currentProfile.id);
          setAllianceRequestCount(requestCount);
        } catch (error) {
          console.error('Error fetching alliance request count:', error);
          setAllianceRequestCount(0);
        } finally {
          setLoadingCount(false);
        }
      } else {
        setLoadingCount(false);
      }
    };

    fetchRequestCount();
  }, [currentProfile?.id, getAcceptedAllianceRequestCount]);

  return (
    <>
      <FreelancerProfileModalContent freelancer={freelancer} className="pt-6 md:pt-0" />

      <hr className="my-4 border-[#E1E9F0]" />

      {freelancer.bio && (
        <div className="mb-4">
          <div className="font-bold text-lg mb-1 text-secondary">
            {t('about', { ns: 'freelancers', defaultValue: 'À propos' })}
          </div>
          <div className="text-secondary mb-8">
            <ProfileBioText bio={freelancer.bio} profileId={freelancer.id} skipRedaction={true} />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 mb-8">
        <label className="text-secondary" htmlFor="allianceRequestMessage">
          <Trans
            i18nKey={'allianceMessageForLabel'}
            ns={'alliances'}
            values={{ freelancerName: freelancer.first_name }}
            components={{ bold: <span className="font-bold" /> }}
          />
        </label>
        <textarea
          name="allianceRequestMessage"
          value={allianceRequestMessage}
          onChange={e => setAllianceRequestMessage(e.target.value)}
          placeholder={t('allianceRequestMessagePlaceholder', { ns: 'alliances' })}
          className="w-full h-[8.3125rem] border-0 px-3 py-2 mt-2 font-ambit text-[0.8125rem] not-italic font-normal leading-[1.25rem] bg-[#E1E9F0] md:h-[5.3125rem]"
          maxLength={1000}
        />
        {allianceRequestError && (
          <div className="text-[#F04438] text-[0.9375rem] font-ambit mt-1">
            {allianceRequestError}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center md:justify-between">
        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button
            className="w-full md:w-auto md:px-8"
            onClick={handleRequestAlliance}
            disabled={
              sendingAllianceRequest ||
              !canRequest ||
              loadingCount ||
              (requiresCredits && !hasEnoughCredits)
            }
          >
            {sendingAllianceRequest
              ? t('sendingAllianceRequest', {
                  ns: 'alliances',
                  defaultValue: 'Envoi de la demande...',
                })
              : primaryAllianceButtonLabel}
          </Button>

          <Button
            className="w-full md:w-auto md:px-8"
            variant="transparent-with-border"
            href={`/freelancers/${freelancer.id}`}
            onClick={onClose}
          >
            {t('viewFullProfile', { ns: 'freelancers', defaultValue: 'Voir le profil complet' })}
          </Button>

          <button
            className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
            onClick={onClose}
          >
            {t('closeFreelancerProfileModal', {
              ns: 'alliances',
              defaultValue: 'Fermer et retourner aux Alliances',
            })}
          </button>
        </div>
        {requiresCredits && canRequest && (
          <WalletSummary
            outerClassName="w-full md:w-full md:max-w-[18rem]"
            modalParams={{
              type: 'allianceRequest',
              freelancerId: freelancer.id,
            }}
          />
        )}
      </div>
      {requiresCredits && canRequest && !hasEnoughCredits && (
        <div className="text-xs text-secondary opacity-70 mt-2">
          {t('allianceRequestCreditsNote', {
            ns: 'alliances',
            defaultValue: `${sendAllianceRequestCost} jeton(s) requis pour envoyer une demande d'alliance (à partir de la 4ème alliance).`,
            count: sendAllianceRequestCost,
          })}
        </div>
      )}
      {requiresCredits && canRequest && hasEnoughCredits && (
        <div className="text-xs text-right text-secondary opacity-70 mt-2">
          {t('allianceRequestCreditsInfo', {
            ns: 'alliances',
            defaultValue: `Cette demande coûtera ${sendAllianceRequestCost} jeton(s).`,
            count: sendAllianceRequestCost,
          })}
        </div>
      )}
    </>
  );
};

interface AllianceRequestedContentProps {
  freelancer: FreelancerSearchResult;
  onClose: () => void;
  allianceRequestId: string;
}

const AllianceRequestedContent = ({
  freelancer,
  onClose,
  allianceRequestId,
}: AllianceRequestedContentProps) => {
  const { t } = useTranslation(['alliances', 'common']);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { deleteAlliance } = useAllianceStore();
  const handleDeleteAlliance = async () => {
    if (!allianceRequestId) return;

    setDeleting(true);
    const result = await deleteAlliance(allianceRequestId);
    if (result.success) {
      setDeleteSuccess(true);
    } else {
      setDeleteError(t('allianceRequestCancelledError', { ns: 'alliances' }));
    }
    setDeleting(false);
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-secondary">
        {deleteSuccess
          ? t('allianceRequestCancelledTitle', {
              ns: 'alliances',
              defaultValue: 'Demande annulée',
            })
          : t('allianceRequestSentTitle', { ns: 'alliances', defaultValue: 'Demande envoyée' })}
      </h2>

      <p className="text-secondary text-[0.9375rem] font-ambit mt-2 mb-12">
        <Trans
          i18nKey={deleteSuccess ? 'allianceRequestCancelledMessage' : 'allianceRequestSentMessage'}
          ns={'alliances'}
          values={{ freelancerName: freelancer.first_name }}
          components={{ bold: <span className="font-bold" /> }}
        />
      </p>

      {deleteError && (
        <div className="text-[#F04438] text-[0.9375rem] font-ambit mt-1">{deleteError}</div>
      )}

      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        <Button className="w-full md:w-auto md:px-8" onClick={onClose}>
          {t('backToAlliances', { ns: 'alliances', defaultValue: 'Retourner à mes Alliances' })}
        </Button>

        {!deleteSuccess && (
          <button
            className={`block font-bold mx-auto text-secondary underline leading-tight md:mx-16`}
            onClick={handleDeleteAlliance}
            disabled={deleting}
          >
            {deleting
              ? t('cancellingRequest', {
                  ns: 'alliances',
                  defaultValue: 'Annulation de la demande...',
                })
              : t('cancelRequest', { ns: 'alliances', defaultValue: 'Annuler la demande' })}
          </button>
        )}
      </div>
    </>
  );
};

interface AllianceRequestModalProps {
  freelancer: FreelancerSearchResult;
  onClose: () => void;
  onAllianceRequested?: () => void;
}

export default function AllianceRequestModal({
  freelancer,
  onClose,
  onAllianceRequested,
}: AllianceRequestModalProps) {
  const { t } = useTranslation(['alliances', 'common']);
  const [allianceRequestId, setAllianceRequestId] = useState<string | null>(null);

  const handleAllianceRequested = (allianceRequestId: string) => {
    setAllianceRequestId(allianceRequestId);
    if (onAllianceRequested) {
      onAllianceRequested();
    }
  };

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { setIsAnyModalOpen } = useModal();

  const closeModal = () => {
    onClose();
    setIsAnyModalOpen(true);
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <div className="flex flex-row items-center">
          <div className="absolute top-2.5 right-1 text-gray-400 hover:text-gray-600 text-2xl  md:right-8">
            <ReportButtonMenu
              onReport={() => setShowReportModal(true)}
              entityOwner={freelancer.id}
              onCopySuccess={handleCopySuccess}
              contentTypeTitle="report_this_user"
            />
          </div>

          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
            onClick={closeModal}
            aria-label={t('common:form_actions.close')}
          >
            &times;
          </button>
        </div>

        {allianceRequestId ? (
          <AllianceRequestedContent
            freelancer={freelancer}
            onClose={closeModal}
            allianceRequestId={allianceRequestId}
          />
        ) : (
          <AllianceRequestContent
            freelancer={freelancer}
            onClose={closeModal}
            onSetAllianceRequestId={handleAllianceRequested}
          />
        )}
      </FullBlockingModal>

      {showReportModal && freelancer && (
        <ReportModal
          entityId={freelancer.id}
          reportedId={freelancer.id}
          contentType="profile"
          onClose={() => setShowReportModal(false)}
        />
      )}
      {copied && <BottomSuccessMessage />}
    </>
  );
}
